import { Spacing } from '@persona-id/design-tokens';
import { XIcon } from '@persona-id/design-tokens/icons/12px';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import {
  REPORT_FILTER_META,
  ReportFilterCondition,
  ReportFilterInputMeta,
  ReportFilterInputType,
} from 'dashboard/lib/reports/filterMetas';
import { ReportTemplate } from 'dashboard/models/report-template';
import Input from 'design-system/components/Input';
import { Label } from 'design-system/components/Text';

const Row = styled('div')`
  width: 100%;
  display: inline-grid;
  align-items: center;
  grid-gap: ${Spacing[3]};
  grid-template-columns: 45px 200px 200px 150px minmax(180px, 1fr) 45px;
  grid-auto-flow: column;
`;

const StyledLabel = styled(Label)`
  min-width: 45px;
`;

const EnumValueMultiSelect = styled(MultiSelect)`
  grid-column: span 2;
`;

const RemoveRow = styled('div')`
  padding: ${Spacing[4]};
  cursor: pointer;
`;
interface InputDisplayProps {
  id: string;
  inputMeta: ReportFilterInputMeta;
  initialValues: string[];
  disableInput: boolean;
  handleValuesChanged: (values: string[]) => void;
}

const InputDisplay = ({
  id,
  inputMeta,
  initialValues,
  disableInput,
  handleValuesChanged,
}: InputDisplayProps) => {
  const [values, setValues] = useState<string[]>(initialValues);
  const singleValue = Array.isArray(values) ? values[0] : values;

  const enumValueTextMapper = useLabelTextMapper(
    inputMeta.options || [],
    inputMeta.label ? inputMeta.label : 'Values',
    {
      emptyIsAll: true,
    },
  );

  const onValuesChanged = (values: string[]) => {
    setValues(values);
    handleValuesChanged(values);
  };

  useEffect(() => {
    onValuesChanged(initialValues);
  }, [initialValues]);

  switch (inputMeta.type) {
    case ReportFilterInputType.Text:
      return (
        <Input
          id={`report-advanced-filter-row__input-value-${id}`}
          type='text'
          onChange={(e) => {
            onValuesChanged(Array.isArray(e.target.value) ? e.target.value : [e.target.value]);
          }}
          placeholder='Enter text'
          value={singleValue}
          disabled={disableInput}
        />
      );
    case ReportFilterInputType.Number:
      return (
        <Input
          id={`report-advanced-filter-row__input-value-${id}`}
          type='number'
          min='0'
          max='1000'
          onChange={(e) => {
            onValuesChanged(Array.isArray(e.target.value) ? e.target.value : [e.target.value]);
          }}
          placeholder='Enter number'
          value={singleValue}
          disabled={disableInput}
        />
      );
    case ReportFilterInputType.Select:
      return (
        <ReactSelect
          id={`report-advanced-filter-row__input-value-${id}`}
          placeholder='Select value'
          value={singleValue}
          options={inputMeta.options || []}
          portal={true}
          disabled={disableInput}
          onChange={onValuesChanged}
        />
      );
    default:
      return (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <EnumValueMultiSelect
          data-test={`report-advanced-filter-row__input-value-${id}`}
          selectOptionValues={inputMeta.options || []}
          labelTextMapper={enumValueTextMapper}
          value={values}
          disabled={disableInput}
          onChange={onValuesChanged}
        />
      );
  }
};

interface ReportAdvancedFilterRowProps {
  id: string;
  label: string;
  row: ReportFilterCondition;
  disableTemplate: boolean;
  templateOptions: OptionType<string>[];
  templateType: ReportTemplate['type'];
  remove: () => void;
  handleTemplateUpdated: (template: string) => void;
  handleRowUpdated: (row: Partial<ReportFilterCondition>) => void;
}

const DEFAULT_INPUT_TYPE = {
  type: ReportFilterInputType.Text,
};

const ReportAdvancedFilterRow = ({
  id,
  label,
  row,
  disableTemplate,
  templateOptions,
  templateType,
  remove,
  handleTemplateUpdated,
  handleRowUpdated,
}: ReportAdvancedFilterRowProps) => {
  const meta = REPORT_FILTER_META[templateType];
  const [filterRow, setFilterRow] = useState<ReportFilterCondition>(row);
  const [operators, setOperators] = useState<OptionType<string>[]>([]);
  const [inputMeta, setInputMeta] = useState<ReportFilterInputMeta>(DEFAULT_INPUT_TYPE);

  const onChange = (newFields: Partial<ReportFilterCondition>) => {
    const updated = {
      ...filterRow,
      ...newFields,
    };
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setFilterRow(updated);
    handleRowUpdated(updated);
  };

  const updateMeta = (property: string, clearValues = false) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const operators = meta.getOperators(property);
    setOperators(operators);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setInputMeta(meta.getInputDisplay(property) ?? DEFAULT_INPUT_TYPE);

    const newFields = {
      property,
      operator: (operators.find((option) => option.value === filterRow.operator) ?? operators[0])
        .value,
    };
    onChange(clearValues ? { ...newFields, values: [] } : newFields);
  };

  useEffect(() => {
    if (templateType) {
      updateMeta(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (meta.properties.find((p) => p.value === filterRow.property) ?? meta.properties[0]).value,
      );
    }
  }, [templateType]);

  return (
    <Row>
      <StyledLabel size='sm'>{label}</StyledLabel>
      <ReactSelect
        data-test='report-advanced-filter-row__template'
        id={`report-advanced-filter-row__template-${id}`}
        options={templateOptions}
        isSearchable={true}
        aria-label='Select template'
        placeholder='Select template'
        value={filterRow.template}
        portal={true}
        disabled={disableTemplate}
        onChange={(template) => {
          handleTemplateUpdated(template);
          setFilterRow({ ...filterRow, template });
        }}
      />
      <ReactSelect
        data-test='report-advanced-filter-row__property'
        id={`report-advanced-filter-row__properties-select-${id}`}
        value={filterRow.property}
        options={meta ? meta.properties : []}
        disabled={(meta ? meta.properties : []).length <= 1}
        portal={true}
        onChange={(property) => updateMeta(property, true)}
      />
      <ReactSelect
        data-test='report-advanced-filter-row__operator'
        id={`report-advanced-filter-row__operator-${id}`}
        options={operators}
        isSearchable={true}
        placeholder='contains'
        value={filterRow.operator}
        disabled={operators.length <= 1}
        portal={true}
        onChange={(operator) => onChange({ operator })}
      />
      {row.operator !== 'is-empty' && (
        <InputDisplay
          id={id}
          inputMeta={inputMeta}
          initialValues={filterRow.values}
          disableInput={isEmpty(row.property)}
          handleValuesChanged={(values) => onChange({ values })}
        />
      )}
      <RemoveRow
        id={`report-advanced-filter-row__remove-${id}`}
        role='button'
        aria-label='Remove filter'
        onClick={remove}
      >
        <XIcon />
      </RemoveRow>
    </Row>
  );
};

export default ReportAdvancedFilterRow;
